import { useEffect, useMemo, useRef, useState } from "react";
import { getEnvironment } from "shared/utils/getEnvironment";
import { getPlatformType } from "shared/utils/platform";
import { captureSentryException } from "shared/services/sentry/captureException";
import { InlineSearch, ParsedInlineConfig } from "shared/services/inLine/types";
import {
  InlineConfigError,
  loadInlineConfig,
} from "shared/services/inLine/inline-config";
import { selectInlineCampaign } from "shared/services/inLine/select-inline";
import { useWindowWidth } from "./useWindowWidth";

export const useInlineBanner = (search: InlineSearch) => {
  const [config, setConfig] = useState<ParsedInlineConfig>({
    campaigns: [],
    issues: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const reportedIssues = useRef(new Set<string>());
  // Subscribe to resize; getPlatformType also detects mobile user agents.
  useWindowWidth();
  const platform =
    getPlatformType() === "Voopter Metasearch (mobile)" ? "mobile" : "desktop";

  useEffect(() => {
    const controller = new AbortController();
    setConfig({ campaigns: [], issues: [] });
    setLoading(true);
    setError(null);
    reportedIssues.current.clear();
    loadInlineConfig(getEnvironment(), controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setConfig(data);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setConfig({ campaigns: [], issues: [] });
        const failure =
          err instanceof Error ? err : new Error("Erro ao carregar inline");
        setError(failure);
        captureSentryException(failure, {
          tags: { feature: "inline-banner", operation: "loadInlineConfig" },
          contexts:
            failure instanceof InlineConfigError
              ? { inlineConfig: failure.details }
              : undefined,
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const {
    originAirport,
    destinationAirport,
    departureDates,
    returnDates,
    adults,
    children,
    babies,
  } = search;
  const selection = useMemo(
    () =>
      selectInlineCampaign(
        config.campaigns,
        {
          originAirport,
          destinationAirport,
          departureDates,
          returnDates,
          adults,
          children,
          babies,
        },
        platform,
      ),
    [
      config.campaigns,
      originAirport,
      destinationAirport,
      departureDates,
      returnDates,
      adults,
      children,
      babies,
      platform,
    ],
  );

  useEffect(() => {
    for (const issue of [...config.issues, ...selection.issues]) {
      const key = `${issue.index}:${issue.error.message}`;
      if (reportedIssues.current.has(key)) continue;
      reportedIssues.current.add(key);
      captureSentryException(issue.error, {
        tags: { feature: "inline-banner", operation: "validateInlineCampaign" },
        contexts: { campaign: { index: issue.index } },
      });
    }
  }, [config.issues, selection.issues]);

  return { inlineData: selection.data, loading, error };
};
